/**
 * BOE-40 — the shell half of the Boer landing contact form.
 *
 * The landing runs in a sandboxed srcdoc frame with an opaque origin, so it
 * posts its validated form here and the shell makes the API call (it already
 * talks to the backend cross-origin for /tenant-apps/resolve). Only the known
 * string fields are forwarded, each capped — the frame is our own document, but
 * a message is still input.
 */
import { API_BASE_URL } from "@/tenant/resolve";
import { BOER_CONTACT_CONTEXTS, BOER_CONTACT_MAX } from "./boerContact";

export const LANDING_CONTACT_PATH = "/website/landing-contact";
/** Key of LANDING_CONTACT_APPS in metaship-backend Services/Website — routes to support@theboer.co.za. */
export const BOER_LANDING_APP = "boer";

const FIELD_CAPS: Record<string, number> = {
  ...BOER_CONTACT_MAX,
  context: 20,
  locale: 10,
  website: 200,
};

export interface LandingContactPayload {
  app: string;
  name: string;
  email: string;
  phone: string;
  farm: string;
  message: string;
  context: string;
  locale: string;
  website: string;
  page: string;
}

/** Keep only the fields the endpoint takes, as capped strings. */
export function sanitizeLandingContact(raw: unknown, page: string): LandingContactPayload {
  const src = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const pick = (key: string): string => {
    const value = src[key];
    return typeof value === "string" ? value.slice(0, FIELD_CAPS[key] ?? 200) : "";
  };
  const context = pick("context");
  return {
    app: BOER_LANDING_APP,
    name: pick("name"),
    email: pick("email"),
    phone: pick("phone"),
    farm: pick("farm"),
    message: pick("message"),
    context: (BOER_CONTACT_CONTEXTS as readonly string[]).includes(context) ? context : "contact",
    locale: pick("locale"),
    website: pick("website"),
    page: page.slice(0, 500),
  };
}

export interface LandingContactResult {
  ok: boolean;
  /** HTTP status, 0 on a network failure. 429 = rate-limited. */
  status: number;
}

/** POST the form. Never throws; the frame only needs ok + status. */
export async function submitLandingContact(
  payload: LandingContactPayload,
  apiBase: string = API_BASE_URL,
  fetchImpl: typeof fetch = fetch,
): Promise<LandingContactResult> {
  try {
    const res = await fetchImpl(`${apiBase}${LANDING_CONTACT_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return { ok: res.ok, status: res.status };
  } catch {
    return { ok: false, status: 0 };
  }
}
